import { useSyncExternalStore } from 'react';
import { AD_MIN_SECONDS } from '../../shared/economy.ts';

/**
 * Rewarded video ads through AdSense's H5 Games Ads (Ad Placement API).
 *
 * The publisher id comes from VITE_ADSENSE_CLIENT ("ca-pub-…") at build time.
 * Without it, development builds show a stand-in test ad so the reward flow can
 * be tried, and production builds hide every ad button.
 *
 * Many players are children, so every request is tagged for child-directed
 * treatment: Google then serves only non-personalised, child-safe ads.
 */

export type AdOutcome = 'viewed' | 'dismissed' | 'unavailable';

const CLIENT: string | undefined = import.meta.env.VITE_ADSENSE_CLIENT || undefined;
const MODE: 'adsense' | 'test' | 'off' = CLIENT ? 'adsense' : import.meta.env.DEV ? 'test' : 'off';

export const adsAvailable = MODE !== 'off';

interface AdBreakDoneInfo {
  breakStatus: string;
}

interface AdBreakParams {
  type: 'reward';
  name: string;
  beforeReward: (showAdFn: () => void) => void;
  adDismissed: () => void;
  adViewed: () => void;
  adBreakDone: (info: AdBreakDoneInfo) => void;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    adBreak?: (params: AdBreakParams) => void;
    adConfig?: (params: Record<string, unknown>) => void;
  }
}

let scriptLoaded = false;

function loadAdSense() {
  if (scriptLoaded || !CLIENT) return;
  scriptLoaded = true;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(CLIENT)}`;
  script.crossOrigin = 'anonymous';
  script.dataset.adClient = CLIENT;
  script.dataset.tagForChildDirectedTreatment = '1';
  if (import.meta.env.DEV) script.dataset.adbreakTest = 'on';
  document.head.appendChild(script);

  window.adsbygoogle = window.adsbygoogle || [];
  window.adBreak = window.adConfig = (params: object) => {
    window.adsbygoogle!.push(params);
  };
  window.adConfig({ preloadAdBreaks: 'on', sound: 'on' });
}

/** Start loading ads early (on the shop page) so the first one is ready on click. */
export function prepareAds() {
  if (MODE === 'adsense') loadAdSense();
}

function showAdSense(name: string): Promise<AdOutcome> {
  loadAdSense();
  return new Promise((resolve) => {
    let outcome: AdOutcome = 'unavailable';
    window.adBreak!({
      type: 'reward',
      name,
      // The player already chose to watch by pressing the button.
      beforeReward: (showAdFn) => showAdFn(),
      adDismissed: () => (outcome = 'dismissed'),
      adViewed: () => (outcome = 'viewed'),
      adBreakDone: () => resolve(outcome),
    });
  });
}

// ------------------------------------------------- stand-in ad (development)

export interface TestAd {
  endsAt: number;
  finish: (outcome: AdOutcome) => void;
}

let testAd: TestAd | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function showTestAd(): Promise<AdOutcome> {
  return new Promise((resolve) => {
    testAd = {
      endsAt: Date.now() + (AD_MIN_SECONDS + 1) * 1000,
      finish: (outcome) => {
        testAd = null;
        emit();
        resolve(outcome);
      },
    };
    emit();
  });
}

/** The stand-in ad on screen right now, for <TestAdDialog>. */
export function useTestAd(): TestAd | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => testAd,
  );
}

/** Show one rewarded ad and report whether it was watched to the end. */
export function showRewardedAd(name: string): Promise<AdOutcome> {
  if (MODE === 'adsense') return showAdSense(name);
  if (MODE === 'test') return showTestAd();
  return Promise.resolve('unavailable');
}
